import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Input from "../../components/ui/input/input";
import MaterialBackDrop from "../../components/ui/loading/materialBackDrop";
import { getAdminPayments } from "../../store/actions/generic";
import { GET_ADMIN_PAYMENTS } from "../../store/actionTypes";
import { schoolCities } from "../../common/constants";

const PAYMENT_TYPE_TEXTS = {
  individual: "Bireysel",
  coach_bulk: "Öğretmen toplu",
  admin: "Admin",
};

// Türkiye saatiyle bugünün tarihi (YYYY-AA-GG)
const todayInTurkey = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" });

const addDays = (dateString, days) => {
  const d = new Date(dateString + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-CA");
};

const formatDate = (dateString) => dateString.split("-").reverse().join(".");

const formatDateTime = (isoString) =>
  new Date(isoString).toLocaleString("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const paymentTypeText = (p) =>
  p.payment_type === "admin" && p.admin_name
    ? "Admin (" + p.admin_name + ")"
    : PAYMENT_TYPE_TEXTS[p.payment_type];

const downloadExcel = (rows, sheetName, filePrefix) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${filePrefix}_${todayInTurkey()}.xlsx`);
};

const cellStyle = { fontWeight: "bold" };
const MAX_TABLE_ROWS = 500; // tablo çok uzarsa sayfa yavaşlamasın, Excel'e tamamı iner

const Payments = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loading[GET_ADMIN_PAYMENTS]);

  const [filters, setFilters] = useState({
    from: addDays(todayInTurkey(), -6),
    to: todayInTurkey(),
    city: "",
    classId: "",
    paymentType: "",
  });
  const [report, setReport] = useState(null);
  const [view, setView] = useState("daily"); // daily || payments || bulk
  const [openOrderId, setOpenOrderId] = useState(null);

  const setFilter = (key, value) => setFilters({ ...filters, [key]: value });

  const applyFilter = () => {
    dispatch(
      getAdminPayments({
        from: filters.from,
        to: filters.to,
        city: filters.city,
        class_id: filters.classId,
        payment_type: filters.paymentType,
      })
    ).then((data) => {
      if (data && data.message) alert(data.message);
      else setReport(data);
    });
  };

  useEffect(() => {
    applyFilter();
  }, []);

  const dateInput = (name, label) => (
    <div style={{ flex: 2, marginRight: "10px" }}>
      <Input
        type="date"
        label={label}
        name={name}
        value={filters[name]}
        onChange={({ target: { value } }) => setFilter(name, value)}
        style={{ height: "55px" }}
      />
    </div>
  );

  const selectInput = (name, label, options) => (
    <div style={{ flex: 2, marginRight: "10px" }}>
      <Input
        name={name}
        placeholder="Tümü"
        label={label}
        select
        value={filters[name]}
        onChange={(e, newItem) => setFilter(name, newItem ? newItem.value : "")}
        options={options}
      />
    </div>
  );

  const filterBar = (
    <div style={{ width: "100%", display: "flex", alignItems: "flex-end" }}>
      {dateInput("from", "Başlangıç")}
      {dateInput("to", "Bitiş")}
      {selectInput(
        "city",
        "İl",
        Object.keys(schoolCities)
          .sort((a, b) => a.localeCompare(b))
          .map((c) => ({ value: c, title: c }))
      )}
      {selectInput(
        "classId",
        "Sınıf",
        [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((c) => ({
          value: c,
          title: c + ". sınıf",
        }))
      )}
      {selectInput(
        "paymentType",
        "Ödeme tipi",
        Object.keys(PAYMENT_TYPE_TEXTS).map((t) => ({
          value: t,
          title: PAYMENT_TYPE_TEXTS[t],
        }))
      )}
      <div style={{ flex: 1, marginBottom: "15px" }}>
        <Button
          style={{ marginLeft: "20px", height: "50px" }}
          variant="contained"
          color="secondary"
          onClick={applyFilter}
        >
          Uygula
        </Button>
      </div>
    </div>
  );

  const headerRow = (columns) => (
    <TableHead>
      <TableRow>
        {columns.map((c) => (
          <TableCell key={c} style={cellStyle}>
            {c}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  // ---------------- Gün gün ----------------
  const dailyTable = () => {
    const totals = { total: 0, individual: 0, coach_bulk: 0, admin: 0 };
    report.daily.forEach((d) =>
      Object.keys(totals).forEach((k) => (totals[k] += d[k]))
    );
    // Gün sayısı arttıkça tablo kaydırılır; TOPLAM satırı altta sabit kalır
    const totalCellStyle = {
      ...cellStyle,
      position: "sticky",
      bottom: 0,
      backgroundColor: "#dd4b18",
    };
    return (
      <Table size="small" stickyHeader>
        {headerRow(["Tarih", "Toplam", "Bireysel", "Öğretmen toplu", "Admin"])}
        <TableBody>
          {[...report.daily].reverse().map((d) => (
            <TableRow key={d.date}>
              <TableCell>{formatDate(d.date)}</TableCell>
              <TableCell style={cellStyle}>{d.total}</TableCell>
              <TableCell>{d.individual}</TableCell>
              <TableCell>{d.coach_bulk}</TableCell>
              <TableCell>{d.admin}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell style={totalCellStyle}>TOPLAM</TableCell>
            <TableCell style={totalCellStyle}>{totals.total}</TableCell>
            <TableCell style={totalCellStyle}>{totals.individual}</TableCell>
            <TableCell style={totalCellStyle}>{totals.coach_bulk}</TableCell>
            <TableCell style={totalCellStyle}>{totals.admin}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const dailyExcel = () =>
    downloadExcel(
      report.daily.map((d) => ({
        Tarih: formatDate(d.date),
        Toplam: d.total,
        Bireysel: d.individual,
        "Öğretmen toplu": d.coach_bulk,
        Admin: d.admin,
      })),
      "Gün Gün Ödemeler",
      "gunluk_odemeler"
    );

  // ---------------- Ödeyenler ----------------
  const paymentsTable = () => (
    <Table size="small">
      {headerRow(["Tarih", "id", "Ad-Soyad", "E-Posta", "Sınıf", "Okul", "İl / İlçe", "Ödeme tipi"])}
      <TableBody>
        {[...report.payments].reverse().slice(0, MAX_TABLE_ROWS).map((p) => (
          <TableRow key={p.receipt_id}>
            <TableCell>{formatDateTime(p.paid_at)}</TableCell>
            <TableCell>{p.user_id}</TableCell>
            <TableCell>{p.name}</TableCell>
            <TableCell>{p.email}</TableCell>
            <TableCell>{p.class_id}</TableCell>
            <TableCell>{p.school_name}</TableCell>
            <TableCell>{p.city + " / " + p.district}</TableCell>
            <TableCell>{paymentTypeText(p)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const paymentsExcel = () =>
    downloadExcel(
      report.payments.map((p) => ({
        Tarih: formatDateTime(p.paid_at),
        ID: p.user_id,
        "Ad-Soyad": p.name,
        "E-Posta": p.email,
        Sınıf: p.class_id,
        Okul: p.school_name,
        İl: p.city,
        İlçe: p.district,
        "Ödeme Tipi": paymentTypeText(p),
        "Sipariş No": p.order_id,
      })),
      "Ödeyenler",
      "odeyenler"
    );

  // ---------------- Öğretmen toplu ödemeleri ----------------
  const payersText = (o) =>
    o.possible_payers.length === 0
      ? "Bilinmiyor"
      : o.possible_payers.map((c) => c.name + " (" + c.email + ")").join(", ");

  const bulkTable = () => {
    const paymentsByOrder = {};
    report.payments.forEach((p) => {
      if (p.payment_type === "coach_bulk")
        (paymentsByOrder[p.order_id] = paymentsByOrder[p.order_id] || []).push(p);
    });

    return (
      <Table size="small">
        {headerRow(["Tarih", "Sipariş No", "Öğrenci", "Okul", "İl / İlçe", "Sınıflar", "Olası ödeyen öğretmen", ""])}
        <TableBody>
          {[...report.bulk_orders].reverse().map((o) => (
            <React.Fragment key={o.order_id}>
              <TableRow>
                <TableCell>{formatDateTime(o.paid_at)}</TableCell>
                <TableCell>{o.order_id}</TableCell>
                <TableCell style={cellStyle}>{o.student_count}</TableCell>
                <TableCell>{o.school_name}</TableCell>
                <TableCell>{o.city + " / " + o.district}</TableCell>
                <TableCell>{o.class_ids.join(", ")}</TableCell>
                <TableCell>{payersText(o)}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setOpenOrderId(openOrderId === o.order_id ? null : o.order_id)
                    }
                  >
                    {openOrderId === o.order_id ? "Gizle" : "Öğrenciler"}
                  </Button>
                </TableCell>
              </TableRow>
              {openOrderId === o.order_id && (
                <TableRow>
                  <TableCell colSpan={8} style={{ backgroundColor: "#f5f5f5" }}>
                    {(paymentsByOrder[o.order_id] || []).map((p) => (
                      <div key={p.user_id}>
                        {p.class_id + ". sınıf - " + p.name + " (" + p.email + ")"}
                      </div>
                    ))}
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    );
  };

  const bulkExcel = () =>
    downloadExcel(
      report.bulk_orders.map((o) => ({
        Tarih: formatDateTime(o.paid_at),
        "Sipariş No": o.order_id,
        "Öğrenci Sayısı": o.student_count,
        Okul: o.school_name,
        İl: o.city,
        İlçe: o.district,
        Sınıflar: o.class_ids.join(", "),
        "Olası Ödeyen Öğretmen": payersText(o),
      })),
      "Öğretmen Toplu Ödemeleri",
      "toplu_odemeler"
    );

  const views = {
    daily: { title: "GÜN GÜN", table: dailyTable, excel: dailyExcel },
    payments: { title: "ÖDEYENLER", table: paymentsTable, excel: paymentsExcel },
    bulk: { title: "ÖĞRETMEN TOPLU ÖDEMELERİ", table: bulkTable, excel: bulkExcel },
  };

  return (
    <div style={{ marginLeft: "30px", width: "100%" }}>
      {loading && <MaterialBackDrop />}
      {filterBar}
      {report && (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "10px 0",
            }}
          >
            <ButtonGroup color="secondary">
              {Object.keys(views).map((v) => (
                <Button
                  key={v}
                  variant={view === v ? "contained" : "outlined"}
                  onClick={() => setView(v)}
                >
                  {views[v].title}
                </Button>
              ))}
            </ButtonGroup>
            <div style={{ backgroundColor: "white", padding: "8px 16px" }}>
              {formatDate(report.from) + " - " + formatDate(report.to) + " arası toplam "}
              <b>{report.count}</b>
              {" ödeme, "}
              <b>{report.bulk_orders.length}</b>
              {" öğretmen toplu siparişi"}
            </div>
            <Button variant="contained" onClick={views[view].excel}>
              Excel indir
            </Button>
          </div>
          <TableContainer
            component={Paper}
            style={view === "daily" ? { maxHeight: "60vh" } : undefined}
          >
            {views[view].table()}
          </TableContainer>
          <div
            style={{
              backgroundColor: "white",
              marginTop: "10px",
              padding: "8px 16px",
              fontSize: "13px",
            }}
          >
            {view === "payments" &&
              report.payments.length > MAX_TABLE_ROWS &&
              <div>* Tabloda son {MAX_TABLE_ROWS} ödeme gösteriliyor, tamamı için Excel indirin.</div>}
            * Ödeme tarihi olarak ödeme sayfasının açıldığı an gösterilir. Öğretmen toplu
            ödemelerinde ödemeyi yapan öğretmen kaydedilmediği için, okulun ilgili
            sınıflarına atanmış öğretmenler gösterilir.
          </div>
        </>
      )}
    </div>
  );
};

export default Payments;
